import React, { useEffect, useMemo, useState } from "react";
import { limit, query } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { PRODUCTS } from "../config";
import {
  LICENSE_CSV_COLUMNS,
  STATUS_META,
  compareBy,
  downloadCsv,
  licenseStatus,
  toCsv,
  todayStamp,
} from "../lib/licenses";
import {
  DISTRIBUTOR_SORTS,
  SEARCH_LIMIT,
  distributorKeyCountQuery,
  distributorKeySearchQuery,
  distributorKeysQuery,
} from "../lib/queries";
import { matchesAllWords, primaryToken, searchWords } from "../lib/search";
import {
  describeQueryError,
  fetchAll,
  useCounts,
  usePagedQuery,
} from "../hooks/usePaged";
import { useLiveQuery } from "../hooks/useLive";
import { Loading } from "./ui";
import LicenseDialog from "./LicenseDialog";
import LicenseTable, { pagingProps } from "./LicenseTable";

const PAGE_SIZE = 25;
const STATUSES = ["issued", "active", "expiring", "expired"];
const COLUMNS = ["key", "status", "type", "customer", "issued", "expiry"];

// One distributor's keys, one product at a time, read a page at a time.
// Used on the distributor's "My keys" page and the admin distributor page.
export default function DistributorKeys({
  distributorId,
  canManage = false,
  actor,
  exportName = "keys",
}) {
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("issued");
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const words = searchWords(term);
  const token = primaryToken(words);
  const searching = !!token;
  const extra = { product: productId };

  const totals = useCounts(
    `dk|${distributorId}`,
    Object.fromEntries(
      PRODUCTS.map((p) => [
        p.id,
        () => distributorKeyCountQuery(p.id, distributorId),
      ]),
    ),
  );
  const paged = usePagedQuery(
    searching ? null : `${distributorId}|${productId}|${status}|${sort}`,
    () => distributorKeysQuery(productId, distributorId, { status, sort }),
    { pageSize: PAGE_SIZE, extra },
  );
  const found = useLiveQuery(
    searching ? `${distributorId}|${productId}|search|${token}` : null,
    () =>
      query(
        distributorKeySearchQuery(productId, distributorId, token),
        limit(SEARCH_LIMIT),
      ),
    extra,
  );
  const searchRows = useMemo(
    () =>
      found.data
        .filter(
          (l) =>
            matchesAllWords(l, words) &&
            (status === "all" || licenseStatus(l) === status),
        )
        .sort(sort === "expiry" ? compareBy("expirySort", "asc") : () => 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found.data, words.join(), status, sort],
  );

  const rows = searching ? searchRows : paged.rows;
  const error = searching ? found.error : paged.error;
  const loading = searching ? found.loading : paged.loading;
  const openLicense = openId ? rows.find((l) => l.id === openId) : null;

  async function exportRows() {
    setExporting(0);
    try {
      const all = searching
        ? searchRows
        : await fetchAll(
            distributorKeysQuery(productId, distributorId, { status, sort }),
            {
              extra,
              onProgress: setExporting,
            },
          );
      const product = PRODUCTS.find((p) => p.id === productId);
      downloadCsv(
        `${exportName}-${product.path}-${todayStamp()}.csv`,
        toCsv(all, LICENSE_CSV_COLUMNS),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(null);
    }
  }

  return (
    <Box>
      <Tabs
        value={productId}
        onChange={(_, next) => {
          setProductId(next);
          setOpenId(null);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {PRODUCTS.map((p) => (
          <Tab
            key={p.id}
            value={p.id}
            label={`${p.name}${totals.counts[p.id] !== undefined ? ` (${totals.counts[p.id].toLocaleString()})` : ""}`}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        ))}
      </Tabs>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <TextField
          placeholder="Search key, customer, location…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          sx={{ flex: "1 1 240px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_META[s].label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {Object.entries(DISTRIBUTOR_SORTS).map(([value, { label }]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<DownloadIcon />}
          disabled={exporting !== null || !rows.length}
          onClick={exportRows}
          sx={{ borderColor: "divider" }}
        >
          {exporting !== null ? `Exporting ${exporting}…` : "Export"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {describeQueryError(error)}
        </Alert>
      )}
      {searching && found.data.length >= SEARCH_LIMIT && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the {SEARCH_LIMIT} most recently issued matches. Add another
          word to narrow the search.
        </Alert>
      )}

      {loading && !rows.length ? (
        <Loading />
      ) : (
        <LicenseTable
          rows={rows}
          columns={COLUMNS}
          onRowClick={(l) => setOpenId(l.id)}
          resetKey={`${productId}|${status}|${sort}|${token}`}
          paging={
            searching
              ? undefined
              : pagingProps(
                  paged,
                  PAGE_SIZE,
                  status === "all" ? totals.counts[productId] : undefined,
                )
          }
          emptyText={
            searching
              ? "No keys match this search."
              : status === "all"
                ? "No keys issued yet."
                : "No keys with this status."
          }
        />
      )}

      {openLicense && (
        <LicenseDialog
          license={openLicense}
          canManage={canManage}
          actor={actor}
          onClose={() => setOpenId(null)}
        />
      )}
    </Box>
  );
}
